/**
 * Вкладка «Контент-план» (Б5.5). Макет — deliverables/ui-design-v1/build/bodies/Plan.html.
 *
 * ЧТО ЭКРАН ДЕЛАЕТ ПОСЛЕ ПРАВОК ЗАКАЗЧИКА 26.08. Это календарь слотов и три действия над
 * ними: перенести пост в другой слот, отменить зарегистрированную публикацию, добавить или
 * снять слот руками. Колонка целей по форматам с экрана убрана — см. `SHOW_FORMAT_TARGETS`.
 *
 * ПЕРЕНОС — ЭТО ПОВТОРНОЕ ОДОБРЕНИЕ, а не правка времени. Начало слота и крайний срок лежат
 * внутри подписанного ключа, поэтому бэкенд отзывает старый ключ и выпускает новый
 * (`domain/slots.ts`). Отсюда два следствия для интерфейса: карточка не «переезжает» до
 * ответа сервера (иначе отказ вернул бы её рывком), а отказ показывается фразой — перенос
 * способен упереться в занятый слот или в изменившееся содержание версии, и человек обязан
 * узнать об этом здесь. Отказа по возрасту материала больше нет: протухание снято со всех
 * путей партией 19.09 (docs/release-2026-09-19.md §6), и перенос на любой срок законен.
 *
 * ПЕРЕНОС НАЗЫВАЕТ БРЕНД И КАНАЛ (2026-10-25, изоляция брендов). Тело несёт `expected_brand` —
 * бренд поста из его модели, обязанный совпасть с брендом календаря, — и `expected_chat_id` —
 * канал текущего режима бренда (`MoveDraftBodySchema` контрактов). Пост чужого бренда в
 * календаре или неподтверждённый канал — переноса нет: подписать новый ключ не на что.
 *
 * ПЕРЕТАСКИВАНИЕ ДУБЛИРУЕТСЯ КЛАВИАТУРОЙ, и это не факультатив: HTML5 drag-and-drop с
 * клавиатуры недоступен вовсе, то есть без второго пути экран был бы неработоспособен без
 * мыши. Оба пути ведут в одно состояние `moving`, поэтому расходиться им негде.
 *
 * Занятость слота определяется полем `state`, а НЕ наличием `draft_id`: у `skipped` и
 * `passed` черновика нет, но свободными они не являются. Переносить можно только версию в
 * `scheduled` — остальные состояния бэкенд отбивает, и предлагать их кнопкой значило бы
 * обещать несуществующее.
 *
 * Времени клиент не считает: подпись слота собирается из `slot_local`, границы периода — из
 * `period_start_local` / `period_end_local`, и обе величины посчитал бэкенд в зоне бренда.
 * `new Date` в UI запрещён правилом R11, а зона бренда попадает сюда только по HTTP (полем
 * `timezone`): из `@smm/*` пакету `ui` доступен единственный `contracts`, конфига бренда он
 * не видит. Проверку «слот в прошлом», «слот занят», «такого слота нет» тоже делает сервер —
 * второй часовой пояс в браузере дал бы второй ответ на тот же вопрос.
 *
 * Формат цели показывается словом из ОБЩЕГО словаря (`components/formatLabel.ts`), код —
 * подсказкой: второй таблицы «код → имя» здесь нет. ЗАВЕСТИ цель можно только на формат,
 * который бренд может заказать (релиз 2026-10-14; с 2026-11-02 — список бренда, `brandFormats`):
 * у GeoBoost новость и дайджест недели, у AGI Boardroom новость и обзор конференции.
 * Свободного поля кода и чипов из `known_formats` больше нет — история бренда помнит снятые
 * форматы (мнение, гайд), и цель на них не заполнил бы никто, а давление плана на ранжирование
 * мониторинга осталось бы. Уже заведённая цель снятого формата видна строкой «… (снят)» и
 * снимается крестиком.
 */

import { msToIso, nowMs } from '@smm/contracts';
import {
  CONTENT_PLAN_MAX_TARGET,
  type ContentPlanPeriod,
  type ContentPlanResponse,
  type MoveDraftBody,
  type MoveDraftResponse,
  postLabel,
} from '@smm/contracts/schemas';
import { type JSX, useCallback, useEffect, useState } from 'react';
import { Link } from 'wouter';
import { ApiRequestError, get, post } from '../api/client.ts';
import {
  type PublicationDispatch,
  type RenderModel,
  RenderModelSchema,
  type ScheduleResponse,
  type ScheduleSlot,
} from '../api/types.ts';
import {
  CHANNEL_BRAND_UNKNOWN_TEXT,
  CHANNEL_LOADING_TEXT,
  CHANNEL_UNKNOWN_TEXT,
  channelMissingText,
  readyChatId,
  useModeChannel,
} from '../api/useModeChannel.ts';
import { useResource } from '../api/useResource.ts';
import { BrandTitle, brandFormats, useBrands } from '../brand.tsx';
import { formatChoiceText, formatLabel } from '../components/formatLabel.ts';
import { IconInfo, IconWarn } from '../components/icons.tsx';
import { Modal } from '../components/Modal.tsx';
import { Shell } from '../components/Shell.tsx';
import { StatusPill } from '../components/StatusPill.tsx';
import { ShellRevWatcher } from '../components/shellSettings.ts';
import { slotLabel } from './decisions/SlotPicker.tsx';
import styles from './PlanPage.module.css';

/** Горизонт вкладки — 4 недели вперёд, тот же, что у SlotPicker (09 §9.4). */
const HORIZON_MS = 28 * 24 * 60 * 60 * 1000;

const LOCAL_MINUTE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

const MONTHS_GEN = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
] as const;

/**
 * Гражданская дата → номер дня от 1970-01-01 (алгоритм Хиннанта). Нужен ровно для одного:
 * разложить слоты по календарным неделям. Считается по `slot_local`, а не по `slot_at`, —
 * неделя у человека местная, и на переводе часов две проекции одного момента разъехались бы.
 *
 * Арифметика вместо `Date` здесь не аскеза: `new Date` в UI запрещён правилом R11, потому что
 * любой момент, собранный на клиенте, рискует не совпасть с моментом, который считает бэкенд.
 */
export function daysFromCivil(year: number, month: number, day: number): number {
  const y = year - (month <= 2 ? 1 : 0);
  const era = Math.floor(y / 400);
  const yoe = y - era * 400;
  const doy = Math.floor((153 * (month + (month > 2 ? -3 : 9)) + 2) / 5) + day - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146097 + doe - 719468;
}

export interface CivilDate {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

/** Обратное преобразование — им подписывается граница недели. */
export function civilFromDays(days: number): CivilDate {
  const z = days + 719468;
  const era = Math.floor(z / 146097);
  const doe = z - era * 146097;
  const yoe = Math.floor(
    (doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365,
  );
  const y = yoe + era * 400;
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153);
  const day = doy - Math.floor((153 * mp + 2) / 5) + 1;
  const month = mp + (mp < 10 ? 3 : -9);
  return { year: y + (month <= 2 ? 1 : 0), month, day };
}

/**
 * Понедельник недели, в которую попадает слот, номером дня. 1970-01-01 — четверг, отсюда
 * сдвиг 3: остаток дня по модулю 7 плюс 3 даёт индекс от понедельника.
 */
export function weekStartOf(slotLocal: string): number | null {
  const m = LOCAL_MINUTE.exec(slotLocal);
  if (m === null) return null;
  const [, y, mo, d] = m as unknown as [string, string, string, string];
  const days = daysFromCivil(Number(y), Number(mo), Number(d));
  const index = ((((days % 7) + 7) % 7) + 3) % 7;
  return days - index;
}

/** Подпись отрезка суток: «24 — 30 августа», а на стыке месяцев — «31 августа — 6 сентября». */
export function rangeLabel(startDays: number, endDays: number): string {
  const a = civilFromDays(startDays);
  const b = civilFromDays(endDays);
  const monthA = MONTHS_GEN[a.month - 1] ?? '';
  const monthB = MONTHS_GEN[b.month - 1] ?? '';
  return a.month === b.month
    ? `${a.day} — ${b.day} ${monthB}`
    : `${a.day} ${monthA} — ${b.day} ${monthB}`;
}

/** Подпись недели — частный случай отрезка длиной семь суток. */
export function weekLabel(weekStart: number): string {
  return rangeLabel(weekStart, weekStart + 6);
}

const LOCAL_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Локальная дата 'YYYY-MM-DD' → номер суток. `null` — строка не дата. */
export function daysOfLocalDate(date: string): number | null {
  const m = LOCAL_DATE.exec(date);
  if (m === null) return null;
  const [, y, mo, d] = m as unknown as [string, string, string, string];
  return daysFromCivil(Number(y), Number(mo), Number(d));
}

/** Подпись одной локальной даты: «24 августа». */
export function dateLabel(local: string): string {
  const d = daysOfLocalDate(local);
  if (d === null) return local;
  const c = civilFromDays(d);
  return `${c.day} ${MONTHS_GEN[c.month - 1] ?? ''}`.trim();
}

/**
 * Подпись периода плана. Границы приходят с сервера уже посчитанными в зоне бренда —
 * клиенту остаётся их прочитать. Нераспознанная дата не прячется: показывается как есть,
 * иначе период с испорченным ключом исчез бы с экрана вместе со своими целями.
 */
export function periodLabel(startLocal: string, endLocal: string): string {
  const a = daysOfLocalDate(startLocal);
  const b = daysOfLocalDate(endLocal);
  return a === null || b === null ? `${startLocal} — ${endLocal}` : rangeLabel(a, b);
}

interface SlotStateView {
  readonly label: string;
  /** Свободный слот — единственный, который рисуется пунктиром и приглушённым. */
  readonly free: boolean;
}

const SLOT_STATE: ReadonlyMap<string, SlotStateView> = new Map<string, SlotStateView>([
  ['free', { label: 'Слот свободен', free: true }],
  ['taken', { label: 'Слот занят', free: false }],
  ['skipped', { label: 'Слот пропущен', free: false }],
  ['passed', { label: 'Слот прошёл', free: false }],
]);

const UNKNOWN_SLOT_STATE: SlotStateView = { label: 'Состояние слота неизвестно', free: false };

interface Week {
  readonly start: number;
  readonly slots: ScheduleSlot[];
}

/** Слоты по календарным неделям, в порядке возрастания. Слот с непонятной минутой не теряется. */
export function groupByWeek(slots: readonly ScheduleSlot[]): Week[] {
  const byStart = new Map<number, ScheduleSlot[]>();
  const unparsed: ScheduleSlot[] = [];
  for (const slot of slots) {
    const start = weekStartOf(slot.slot_local);
    if (start === null) {
      unparsed.push(slot);
      continue;
    }
    const bucket = byStart.get(start);
    if (bucket === undefined) byStart.set(start, [slot]);
    else bucket.push(slot);
  }
  const weeks = [...byStart.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([start, ws]): Week => ({ start, slots: ws }));
  return unparsed.length === 0 ? weeks : [...weeks, { start: Number.NaN, slots: unparsed }];
}

// ─────────────────────────────────────────────────────────────────────────────
// Перенос поста между слотами

/**
 * Пост, который сейчас переносят. Одно состояние на оба пути — мышь и клавиатуру: два
 * отдельных состояния разошлись бы на первом же отказе сервера.
 */
export interface MoveSource {
  readonly slotId: string;
  readonly draftId: string;
  readonly versionNo: number;
  readonly title: string;
  readonly fromLocal: string;
}

/**
 * Можно ли перенести пост из этого слота. `scheduled` — единственное состояние версии,
 * которое бэкенд переносит; кнопка на остальных обещала бы то, чего маршрут не делает.
 */
export function moveSourceOf(slot: ScheduleSlot): MoveSource | null {
  if (slot.state !== 'taken' || slot.status !== 'scheduled') return null;
  if (slot.draft_id === null || slot.version_no === null || slot.post_no === null) return null;
  return {
    slotId: slot.slot_id,
    draftId: slot.draft_id,
    versionNo: slot.version_no,
    title: slot.title ?? postLabel(slot.post_no),
    fromLocal: slot.slot_local,
  };
}

/**
 * Фраза об отказе переноса. Главное здесь — сказать, ГДЕ ОСТАЛСЯ ПОСТ, а не только что
 * пошло не так: перенос состоит из снятия и повторного одобрения, и между ними есть окно,
 * в котором пост уже снят со старого слота, а новый ключ ещё не выпущен. Бэкенд метит этот
 * исход полем `unscheduled_from` и сам дописывает объяснение в сообщение — второй раз о том
 * же здесь не пишем.
 *
 * В отказах предпроверки (занятый слот, изменившееся содержание) до первой
 * записи дело не дошло, и пост стоит там же, где стоял. Это и есть та строка, ради которой
 * функция существует: молча вернуть карточку на место — значит оставить человека гадать,
 * перенёсся пост или нет.
 *
 * И третий случай, который нельзя путать с первыми двумя: исход НЕИЗВЕСТЕН (M-003). 500 и
 * обрыв связи ничего не обещают — обработчик мог вылететь и до записи, и после снятия с
 * прежнего слота. Обещать здесь «пост остался на прежнем слоте» значит уверенно сказать
 * человеку неправду ровно в тот момент, когда он больше всего полагается на эту строку.
 */
export function moveErrorText(e: unknown): string {
  if (e instanceof ApiRequestError) {
    const d = e.details;
    const halfway = typeof d === 'object' && d !== null && 'unscheduled_from' in d;
    if (halfway) return `Перенос не завершён: ${e.message}.`;
    if (unknownOutcome(e)) return `Перенос не выполнен: ${e.message}. ${CHECK_GRID}`;
    return `Перенос не выполнен: ${e.message}. Пост остался на прежнем слоте.`;
  }
  const text = e instanceof Error ? e.message : String(e);
  return `Перенос не выполнен: ${text}. ${CHECK_GRID}`;
}

/** Фраза для исхода, которого бэкенд не обещал. Сетка перечитывается тут же и покажет правду. */
const CHECK_GRID = 'Что успел сделать бэкенд, отсюда не видно — сверьтесь с сеткой.';

/**
 * Отказ, про который НЕЛЬЗЯ сказать «до первой записи дело не дошло». Это 5xx (обработчик
 * вылетел где угодно, в том числе уже после снятия с прежнего слота) и обрыв связи
 * (`client.ts` отдаёт статус 0: ответ не доехал, а транзакция на сервере могла пройти
 * целиком). Обещание «пост остался на прежнем слоте» на этих исходах — выдумка.
 */
function unknownOutcome(e: ApiRequestError): boolean {
  return e.status >= 500 || e.status === 0 || e.code === 'internal_error';
}

// ─────────────────────────────────────────────────────────────────────────────
// Цели по типам публикаций
//
// ВЫКЛЮЧЕНО РЕШЕНИЕМ ЗАКАЗЧИКА 26.08.2026: контент-план вводится свой, стоковый, и посты
// генерируются по нему, а не по целям, набранным здесь руками. Код целей оставлен целиком и
// продолжает проверяться тестами (`PeriodCard` рендерится в PlanPage.test.tsx напрямую) —
// возврат колонки на экран это правка ОДНОЙ СТРОКИ ниже, а не восстановление удалённого.
//
// Тип объявлен `boolean`, а не выведен из литерала, намеренно: с литеральным `false`
// компилятор считает вторую ветку недостижимой и перестаёт её проверять — то есть выключенный
// экран тихо сгнил бы к моменту, когда его понадобится вернуть.

const SHOW_FORMAT_TARGETS: boolean = false;

/** Длина периода. Горизонт плана — 2–4 недели (Б1.8); неделя оставлена как частный случай. */
const WEEK_CHOICES = [1, 2, 4] as const;
const WEEK_LABEL: Readonly<Record<number, string>> = {
  1: '1 неделя',
  2: '2 недели',
  4: '4 недели',
};

interface TargetRow {
  readonly format: string;
  /** null — цели нет; это НЕ ноль: ноль означает «в этом периоде формат не публикуем». */
  readonly target: number | null;
  readonly committed: number;
}

/**
 * Строки карточки: цели периода, поверх которых лежат несохранённые правки. Формат,
 * занимающий слоты мимо плана, остаётся в списке даже после снятия цели — иначе он исчез
 * бы с экрана, продолжая расходовать слоты.
 */
export function mergeTargets(
  period: ContentPlanPeriod,
  edits: ReadonlyMap<string, number | null>,
): TargetRow[] {
  const rows = new Map<string, TargetRow>();
  for (const t of period.targets) {
    rows.set(t.format_code, {
      format: t.format_code,
      target: t.target_count,
      committed: t.committed_count,
    });
  }
  for (const [format, value] of edits) {
    const prev = rows.get(format);
    if (value === null) {
      if (prev !== undefined && prev.committed > 0) rows.set(format, { ...prev, target: null });
      else rows.delete(format);
      continue;
    }
    rows.set(format, { format, target: value, committed: prev?.committed ?? 0 });
  }
  return [...rows.values()].sort((a, b) => a.format.localeCompare(b.format));
}

/** Фраза о расхождении. Возвращает null, когда цели ровно покрывают сетку. */
export function deltaNote(targetsTotal: number, slotsTotal: number): string | null {
  const delta = targetsTotal - slotsTotal;
  if (delta === 0) return null;
  return delta > 0
    ? `Целей ${targetsTotal} при ${slotsTotal} слотах: ${delta} не выйдет — часть форматов вытеснится. ` +
        'Уменьшите цели или добавьте слот в конфиге бренда.'
    : `Целей ${targetsTotal} при ${slotsTotal} слотах: ${-delta} слотов останутся пустыми, ` +
        'их доберёт вечнозелёный запас.';
}

export function PeriodCard({
  brand,
  period,
  formats,
  onSaved,
}: {
  brand: string;
  period: ContentPlanPeriod;
  /**
   * Что бренд может заказать (`brandFormats`): только на эти форматы заводится цель. Пропом, а
   * не из провайдера: карточку тесты рисуют напрямую, без списка брендов.
   */
  formats: readonly string[];
  onSaved: () => void;
}): JSX.Element {
  const [edits, setEdits] = useState<ReadonlyMap<string, number | null>>(() => new Map());
  const [weeks, setWeeks] = useState<number>(period.period_weeks);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rows = mergeTargets(period, edits);
  const total = rows.reduce((sum, r) => sum + (r.target ?? 0), 0);
  const dirty = edits.size > 0 || weeks !== period.period_weeks;
  const note = deltaNote(total, period.slots_total);

  const setTarget = (format: string, value: number | null): void => {
    setEdits((prev) => {
      const next = new Map(prev);
      next.set(format, value);
      return next;
    });
  };

  const save = (): void => {
    if (busy) return;
    setBusy(true);
    setError(null);
    post<unknown>('/api/v1/content-plan', {
      brand,
      period_start_local: period.period_start_local,
      period_weeks: weeks,
      targets: rows
        .filter((r) => r.target !== null)
        .map((r) => ({ format_code: r.format, target_count: r.target })),
    })
      .then(() => {
        setEdits(new Map());
      })
      .catch((e: unknown) => {
        setError(e instanceof ApiRequestError || e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setBusy(false);
        // Ответ уже несёт период, но экран перечитывает его целиком: соседние периоды
        // делят с этим одни и те же слоты, и показывать их по прежним числам нельзя.
        onSaved();
      });
  };

  // Только форматы, которые бренд может заказать, и только те, у которых строки в периоде ещё
  // нет (или она снята без занятых слотов — тогда её нет в `rows`).
  const unused = formats.filter((f) => !rows.some((r) => r.format === f));

  return (
    <div className="card-flat stack" style={{ gap: 10 }}>
      <div className={styles['weekHead']}>
        <div className="h3">{periodLabel(period.period_start_local, period.period_end_local)}</div>
        <div style={{ flex: 1 }} />
        <div className="seg">
          {WEEK_CHOICES.map((w) => (
            <button
              key={w}
              type="button"
              className={`seg-o${w === weeks ? ' on' : ''}`}
              onClick={() => {
                setWeeks(w);
              }}
            >
              {WEEK_LABEL[w] ?? `${w} нед`}
            </button>
          ))}
        </div>
      </div>

      {period.covered_by_period_start === null ? null : (
        <div className="micro">
          Неделя уже входит в период с {dateLabel(period.covered_by_period_start)} — цели,
          заведённые здесь, планируют её второй раз.
        </div>
      )}

      {rows.length === 0 ? (
        <div className="val val-info">
          <IconInfo />
          <span>
            {`Целей на этот период нет. Заведите первую кнопкой ниже: ${formatChoiceText(formats)}.`}
          </span>
        </div>
      ) : (
        <div className="stack" style={{ gap: 2 }}>
          {rows.map((row) => (
            <div key={row.format} className={`vrow ${styles['targetRow']}`}>
              <span className={styles['formatCode']} title={row.format}>
                {formatLabel(row.format)}
              </span>
              {row.target === null ? (
                <span className="meta" style={{ width: 122, flex: 'none' }}>
                  цели нет
                </span>
              ) : (
                <span className={styles['stepper']}>
                  <button
                    type="button"
                    className="edbtn"
                    aria-label={`меньше ${row.format}`}
                    onClick={() => {
                      setTarget(row.format, Math.max(0, (row.target ?? 0) - 1));
                    }}
                  >
                    −
                  </button>
                  <span className="mono num" data-testid={`target-${row.format}`}>
                    {row.target}
                  </span>
                  <button
                    type="button"
                    className="edbtn"
                    aria-label={`больше ${row.format}`}
                    onClick={() => {
                      setTarget(
                        row.format,
                        Math.min(CONTENT_PLAN_MAX_TARGET, (row.target ?? 0) + 1),
                      );
                    }}
                  >
                    +
                  </button>
                </span>
              )}
              <span className="meta num" style={{ flex: 1 }}>
                занято {row.committed}
              </span>
              {row.target === null ? null : (
                <button
                  type="button"
                  className="edbtn"
                  aria-label={`убрать ${row.format}`}
                  onClick={() => {
                    setTarget(row.format, null);
                  }}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {unused.length === 0 ? null : (
        <div className={styles['addRow']}>
          {unused.map((f) => (
            <button
              key={f}
              type="button"
              className="chip-plain"
              onClick={() => {
                setTarget(f, 1);
              }}
            >
              + {formatLabel(f)}
            </button>
          ))}
        </div>
      )}

      <div className="meta num">
        Сумма целей {total} · слотов в периоде {period.slots_total} (свободных {period.slots_free})
        {period.committed_unformatted === 0 ? '' : ` · без формата ${period.committed_unformatted}`}
      </div>

      {note === null ? null : (
        <div className={total > period.slots_total ? 'val val-warn' : 'val val-info'}>
          {total > period.slots_total ? <IconWarn /> : <IconInfo />}
          <span>{note}</span>
        </div>
      )}

      {error === null ? null : (
        <div className="val val-err">
          <span>План не сохранился: {error}</span>
        </div>
      )}

      <div className={styles['saveRow']}>
        <button
          type="button"
          className={`btn btn-sm ${dirty && !busy ? 'btn-pri' : 'btn-dis'}`}
          disabled={!dirty || busy}
          onClick={save}
        >
          Сохранить план
        </button>
        <span className="micro">
          {dirty ? 'есть несохранённые правки' : 'агент увидит план на следующем прогоне'}
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Строка слота

interface SlotHandlers {
  readonly beginMove: (source: MoveSource) => void;
  readonly endMove: () => void;
  readonly hover: (slotId: string | null) => void;
  readonly dropOn: (slot: ScheduleSlot) => void;
  readonly cancelPublication: (source: MoveSource) => void;
  readonly removeSlot: (slot: ScheduleSlot) => void;
}

function SlotRow({
  brand,
  slot,
  editing,
  busy,
  moving,
  target,
  on,
}: {
  readonly brand: string;
  readonly slot: ScheduleSlot;
  readonly editing: boolean;
  readonly busy: boolean;
  readonly moving: MoveSource | null;
  /** Слот подсвечен как место падения: курсор над ним либо на нём фокус клавиатуры. */
  readonly target: boolean;
  readonly on: SlotHandlers;
}): JSX.Element {
  const view = SLOT_STATE.get(slot.state) ?? UNKNOWN_SLOT_STATE;
  const source = moveSourceOf(slot);
  const dragging = moving !== null;
  const isSource = moving !== null && moving.slotId === slot.slot_id;
  const droppable = view.free && dragging && !isSource;

  const cls = [
    styles['slotRow'],
    view.free ? '' : styles['slotTaken'],
    editing ? styles['slotEditing'] : '',
    isSource ? styles['slotSource'] : '',
    target && droppable ? styles['slotTarget'] : '',
    source !== null && !dragging ? styles['slotDraggable'] : '',
  ]
    .filter((c) => c !== '')
    .join(' ');

  /*
   * Подпись занятого слота: заголовок темы, а без него — номер поста («post #N»).
   * Если нет и черновика — `skipped` и `passed` как раз такие, — подписью становится само
   * состояние, и тогда оно НЕ повторяется строкой ниже: два одинаковых слова в строке
   * читаются как ошибка данных.
   */
  const title = slot.title ?? (slot.post_no === null ? null : postLabel(slot.post_no));

  return (
    // Строка списка, а не div: неделя — это список строк расписания, и объявленный список
    // даёт скринридеру число слотов и позицию в нём. Роли «цель перетаскивания» в ARIA нет
    // вовсе — за неё отвечает второй, клавиатурный путь.
    <li
      className={cls}
      data-testid={`slot-${slot.slot_id}`}
      draggable={source !== null}
      onDragStart={(e) => {
        if (source === null) return;
        // Firefox не начинает перетаскивание, пока в буфере пусто. Сам буфер нам не нужен:
        // что переносим, знает состояние экрана, а не событие.
        e.dataTransfer.setData('text/plain', slot.slot_id);
        e.dataTransfer.effectAllowed = 'move';
        on.beginMove(source);
      }}
      onDragEnd={() => {
        on.endMove();
      }}
      onDragOver={(e) => {
        if (!droppable) return;
        // Без preventDefault браузер считает слот запрещённой зоной и события drop не будет.
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        on.hover(slot.slot_id);
      }}
      onDragLeave={() => {
        if (droppable) on.hover(null);
      }}
      onDrop={(e) => {
        if (!droppable) return;
        e.preventDefault();
        on.dropOn(slot);
      }}
    >
      <div className={`${styles['slotTime']} ${view.free ? styles['slotTimeFree'] : ''}`}>
        <span>{slotLabel(slot.slot_local)}</span>
        {slot.ambiguous ? (
          <span className="chip-plain" style={{ fontSize: 11 }} title="перевод часов назад">
            минута повторяется
          </span>
        ) : null}
      </div>

      <div className={styles['slotBody']}>
        {view.free ? (
          <>
            <span className="small" style={{ color: 'var(--muted)' }}>
              {droppable ? 'Отпустите здесь' : view.label}
            </span>
            {droppable ? null : (
              <span className="meta">пост встанет сюда только через утверждение в бэклоге</span>
            )}
          </>
        ) : (
          <>
            {slot.draft_id === null ? (
              <span className="small" style={{ color: 'var(--muted)' }}>
                {title ?? view.label}
              </span>
            ) : (
              <Link
                href={`/b/${brand}/draft/${slot.draft_id}`}
                className={`small ${styles['slotTitle']}`}
                draggable={false}
              >
                {title ?? view.label}
              </Link>
            )}
            <span className="meta">
              {[
                ...(title === null ? [] : [view.label]),
                ...(slot.version_no === null ? [] : [`v${slot.version_no}`]),
              ].join(' · ')}
            </span>
          </>
        )}
      </div>

      <div className={styles['slotActions']}>
        {slot.status === null ? null : <StatusPill status={slot.status} />}

        {droppable ? (
          <button
            type="button"
            className={busy ? 'btn btn-sm btn-dis' : 'btn btn-sm btn-pri'}
            disabled={busy}
            aria-label={`Перенести на ${slotLabel(slot.slot_local)}`}
            onFocus={() => {
              on.hover(slot.slot_id);
            }}
            onClick={() => {
              on.dropOn(slot);
            }}
          >
            Сюда
          </button>
        ) : null}

        {source === null ? null : (
          /*
           * Один и тот же элемент кнопки меняет подпись, а не сменяется вторым: React
           * переиспользует DOM-узел на прежнем месте, и фокус клавиатуры остаётся там, где
           * человек его поставил. Размонтируй мы «Перенести» ради «Отмены» — фокус уехал бы
           * на body ровно в тот момент, когда человеку нужно выбирать слот.
           */
          <button
            type="button"
            className={busy ? 'btn btn-sm btn-dis' : 'btn btn-sm btn-ghost'}
            disabled={busy}
            aria-label={
              isSource ? `Отменить перенос «${source.title}»` : `Перенести «${source.title}»`
            }
            onClick={() => {
              if (isSource) on.endMove();
              else on.beginMove(source);
            }}
          >
            {isSource ? 'Отмена' : 'Перенести'}
          </button>
        )}

        {source !== null && !dragging ? (
          <button
            type="button"
            className={busy ? 'btn btn-sm btn-dis' : 'btn btn-sm btn-ghost'}
            disabled={busy}
            aria-label={`Отменить публикацию «${source.title}»`}
            onClick={() => {
              on.cancelPublication(source);
            }}
          >
            Отменить публикацию
          </button>
        ) : null}

        {editing && view.free && !dragging ? (
          <button
            type="button"
            className={busy ? 'btn btn-sm btn-dis' : 'btn btn-sm btn-ghost'}
            disabled={busy}
            aria-label={`Убрать слот ${slotLabel(slot.slot_local)}`}
            onClick={() => {
              on.removeSlot(slot);
            }}
          >
            Убрать
          </button>
        ) : null}
      </div>
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

interface Notice {
  readonly tone: 'ok' | 'err';
  readonly text: string;
}

export function PlanPage({ brand }: { brand: string }): JSX.Element {
  // Диапазон считается один раз за жизнь компонента: пересчёт на каждом рендере менял бы
  // строку пути, а она — зависимость загрузки, то есть запросы пошли бы по кругу.
  const [range] = useState(() => {
    const from = nowMs();
    return { from: msToIso(from), to: msToIso(from + HORIZON_MS) };
  });

  // Обе половины экрана просят ОДИН горизонт: цели, показанные не за тот период, что
  // сетка, сравнивать не с чем.
  const horizon = `brand=${encodeURIComponent(brand)}&from=${encodeURIComponent(
    range.from,
  )}&to=${encodeURIComponent(range.to)}`;
  const schedule = useResource<ScheduleResponse>(`/api/v1/schedule?${horizon}`);
  // Выключенная колонка не грузит своих данных: запрос, результат которого никто не
  // показывает, — это лишний вес на каждом открытии экрана.
  const plan = useResource<ContentPlanResponse>(
    SHOW_FORMAT_TARGETS ? `/api/v1/content-plan?${horizon}` : null,
  );
  // Цели заводятся только на форматы, которые бренд может заказать (шапка файла).
  const formats = brandFormats(useBrands(), brand);

  const [moving, setMoving] = useState<MoveSource | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [editing, setEditing] = useState(false);
  const [newSlot, setNewSlot] = useState('');
  const [cancelling, setCancelling] = useState<MoveSource | null>(null);

  const stopMove = useCallback(() => {
    setMoving(null);
    setHovered(null);
  }, []);

  // Escape отменяет перенос — тот же выход, что у диалогов. Без него режим переноса,
  // включённый с клавиатуры, выключался бы только успешным падением в слот.
  useEffect(() => {
    if (moving === null) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') stopMove();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [moving, stopMove]);

  const { refetch: refetchSchedule } = schedule;
  // Режим — этого бренда (docs/multi-brand.md §3): перенос подписывается на канал его режима.
  const dispatch = useResource<PublicationDispatch>(
    `/api/v1/publication-dispatch?brand=${encodeURIComponent(brand)}`,
  );
  const mode = dispatch.data?.state;
  // Канал режима — часть тела переноса (`expected_chat_id`): тот же источник, что у одобрения.
  const { channel, refetch: refetchChannel } = useModeChannel(brand);
  const { refetch: refetchDispatch } = dispatch;
  // Шапка сообщила «настройки изменились»: выключатель автоодобрения мог освободить слоты, смена
  // режима — сменить режим и канал, которые называет тело переноса (`expected_mode`,
  // `expected_chat_id`). Перечитывается всё, от чего перенос зависит, а не одна сетка: иначе
  // после переключения режима в шапке каждый перенос отказывал бы до перезагрузки.
  const onShellRev = useCallback((): void => {
    refetchSchedule();
    refetchDispatch();
    refetchChannel();
  }, [refetchSchedule, refetchDispatch, refetchChannel]);

  /**
   * Перенос. Хэш содержимого читается отдельным запросом версии: строка расписания его не
   * несёт, а тело переноса без него бэкенд не принимает (`expected_content_hash` —
   * доказательство, что переносится ровно та версия, что показана).
   */
  const dropOn = (target: ScheduleSlot): void => {
    const src = moving;
    if (src === null || busy) return;
    // Режим публикаций решает канал НОВОГО ключа, и бэкенд не примет перенос, не зная, какой
    // режим видел человек (`expected_mode`). Режим не загрузился — переноса нет: неизвестно,
    // на какой канал он подпишет пост.
    if (mode === undefined) {
      setNotice({
        tone: 'err',
        text: 'Перенос не выполнен: режим публикаций не загрузился, и неизвестно, в какой канал уйдёт пост. Обновите экран.',
      });
      stopMove();
      return;
    }
    const chatId = readyChatId(channel);
    if (chatId === null) {
      const why =
        channel.kind === 'missing'
          ? channelMissingText(mode)
          : channel.kind === 'unknown'
            ? CHANNEL_BRAND_UNKNOWN_TEXT
            : channel.kind === 'loading'
              ? CHANNEL_LOADING_TEXT
              : CHANNEL_UNKNOWN_TEXT;
      setNotice({
        tone: 'err',
        text: `Перенос не выполнен. ${why} Пост остался на прежнем слоте.`,
      });
      stopMove();
      return;
    }
    setBusy(true);
    setNotice(null);
    get<unknown>(`/api/v1/drafts/${src.draftId}/versions/${String(src.versionNo)}`)
      .then((raw): Promise<MoveDraftResponse> => {
        const parsed = RenderModelSchema.safeParse(raw);
        if (!parsed.success) {
          throw new Error('версия поста не прочиталась — откройте её и повторите');
        }
        const model: RenderModel = parsed.data;
        if (model.brand !== brand) {
          // Календарь бренда показал пост другого бренда — ответ, которого не бывает; ключ на
          // канал этого календаря ему подписывать нельзя.
          throw new Error(`пост принадлежит бренду ${model.brand}, а календарь — ${brand}`);
        }
        const body: MoveDraftBody = {
          slot_at: target.slot_at,
          expected_content_hash: model.content_hash,
          expected_mode: mode,
          expected_brand: model.brand,
          expected_chat_id: chatId,
        };
        return post<MoveDraftResponse>(
          `/api/v1/drafts/${src.draftId}/versions/${String(src.versionNo)}/move`,
          body,
        );
      })
      .then(() => {
        setNotice({ tone: 'ok', text: `Перенесли на ${slotLabel(target.slot_local)}.` });
      })
      .catch((e: unknown) => {
        setNotice({ tone: 'err', text: moveErrorText(e) });
        // 409 — на сервере не то, что видел календарь: режим или канал могли сменить в другой
        // вкладке или другим админом (тогда `rev` этой шапки не растёт). Перечитываем оба, как
        // диалог одобрения, — иначе повтор отказал бы так же.
        if (e instanceof ApiRequestError && e.status === 409) {
          refetchDispatch();
          refetchChannel();
        }
      })
      .finally(() => {
        setBusy(false);
        stopMove();
        // Сетка перечитывается в обоих исходах: при отказе она подтверждает, что пост
        // остался на месте, при половинном — что старый слот уже свободен.
        refetchSchedule();
      });
  };

  const cancelPublication = (src: MoveSource): void => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    setCancelling(null);
    post<unknown>(`/api/v1/drafts/${src.draftId}/versions/${String(src.versionNo)}/unschedule`, {})
      .then(() => {
        setNotice({ tone: 'ok', text: 'Публикация отменена, слот свободен.' });
      })
      .catch((e: unknown) => {
        const text = e instanceof ApiRequestError || e instanceof Error ? e.message : String(e);
        setNotice({ tone: 'err', text: `Публикация не отменена: ${text}.` });
      })
      .finally(() => {
        setBusy(false);
        refetchSchedule();
      });
  };

  const removeSlot = (slot: ScheduleSlot): void => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    post<void>(`/api/v1/slots/${slot.slot_id}/remove`, {})
      .then(() => {
        setNotice({ tone: 'ok', text: `Слот ${slotLabel(slot.slot_local)} снят.` });
      })
      .catch((e: unknown) => {
        const text = e instanceof ApiRequestError || e instanceof Error ? e.message : String(e);
        setNotice({ tone: 'err', text: `Слот не снят: ${text}.` });
      })
      .finally(() => {
        setBusy(false);
        refetchSchedule();
      });
  };

  const addSlot = (): void => {
    if (busy || !LOCAL_MINUTE.test(newSlot)) return;
    setBusy(true);
    setNotice(null);
    post<unknown>('/api/v1/slots', { brand, slot_local: newSlot })
      .then(() => {
        setNotice({ tone: 'ok', text: `Слот ${slotLabel(newSlot)} добавлен.` });
        setNewSlot('');
      })
      .catch((e: unknown) => {
        const text = e instanceof ApiRequestError || e instanceof Error ? e.message : String(e);
        setNotice({ tone: 'err', text: `Слот не добавлен: ${text}.` });
      })
      .finally(() => {
        setBusy(false);
        refetchSchedule();
      });
  };

  const slots = schedule.data?.slots ?? [];
  const weeks = groupByWeek(slots);
  const periods = plan.data?.periods ?? [];
  const timezone = schedule.data?.timezone;
  const newSlotReady = LOCAL_MINUTE.test(newSlot);

  const handlers: SlotHandlers = {
    beginMove: (source) => {
      setNotice(null);
      setMoving(source);
      setHovered(null);
    },
    endMove: stopMove,
    hover: setHovered,
    dropOn,
    cancelPublication: setCancelling,
    removeSlot,
  };

  return (
    <Shell brand={brand} active="plan">
      {/* Шапка сообщила «настройки изменились» — перечитать сетку, режим и канал (`onShellRev`). */}
      <ShellRevWatcher onRev={onShellRev} />
      <div className={styles['page']}>
        <div className={styles['head']}>
          <div className={styles['headText']}>
            <div className="h1">Контент-план</div>
            <div className="meta">
              <BrandTitle id={brand} /> · сетка слотов на 4 недели вперёд
              {timezone === undefined ? '' : ` · зона ${timezone}`}
            </div>
          </div>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className={`btn btn-sm ${editing ? 'btn-pri' : 'btn-ghost'}`}
            aria-pressed={editing}
            onClick={() => {
              setEditing((v) => !v);
              stopMove();
            }}
          >
            {editing ? 'Готово' : 'Настроить слоты'}
          </button>
        </div>

        {notice === null ? null : (
          <div className={notice.tone === 'ok' ? 'val val-info' : 'val val-err'}>
            {notice.tone === 'ok' ? <IconInfo /> : <IconWarn />}
            <span>{notice.text}</span>
          </div>
        )}

        {!editing ? null : (
          <div className="card-flat stack" style={{ gap: 10 }}>
            <div className="h3">Настройка слотов</div>
            <div className={styles['setupRow']}>
              <input
                type="datetime-local"
                step={60}
                className={styles['slotInput']}
                aria-label="время нового слота"
                value={newSlot}
                onChange={(e) => {
                  setNewSlot(e.target.value);
                }}
              />
              <button
                type="button"
                className={`btn btn-sm ${newSlotReady && !busy ? 'btn-pri' : 'btn-dis'}`}
                disabled={!newSlotReady || busy}
                onClick={addSlot}
              >
                Добавить слот
              </button>
              <span className="micro">
                время канала{timezone === undefined ? '' : ` · ${timezone}`}
              </span>
            </div>
            <div className="micro">
              Убрать можно только свободный слот: занятый держит выпущенный ключ — сначала отмените
              публикацию. Снятый слот вернётся, если он есть в недельном шаблоне бренда.
            </div>
          </div>
        )}

        {moving === null ? null : (
          <div className={styles['moveBar']}>
            <span className="small" style={{ color: 'var(--ink)', fontWeight: 600 }}>
              Переносим: {moving.title}
            </span>
            <span className="micro">с {slotLabel(moving.fromLocal)} — выберите свободный слот</span>
            <span style={{ flex: 1 }} />
            <button type="button" className="btn btn-sm btn-ghost" onClick={stopMove}>
              Отмена
            </button>
          </div>
        )}

        <div className={SHOW_FORMAT_TARGETS ? styles['cols'] : styles['single']}>
          {/* ══ цели по типам публикаций — выключены, см. SHOW_FORMAT_TARGETS ══ */}
          {SHOW_FORMAT_TARGETS ? (
            <div className={styles['targets']}>
              <div className="card-flat stack" style={{ gap: 4 }}>
                <div className="h3">Цели по типам публикаций</div>
                <div className="micro">
                  Правит человек; агент читает план на следующем прогоне и сверяет с ним выбор тем.
                  Цель — не квота: она говорит, чего в периоде должно быть больше, а не запрещает
                  остальное.
                </div>
              </div>

              {plan.loading ? <div className="micro">Загружаем цели…</div> : null}

              {plan.error === undefined ? null : (
                <div className="val val-err">
                  <span>Цели не загрузились: {plan.error.message}</span>
                </div>
              )}

              {!plan.loading && plan.error === undefined && periods.length === 0 ? (
                <div className="val val-info">
                  <IconInfo />
                  <span>
                    Периодов на горизонте нет: план заводится на неделю, начинающуюся с
                    понедельника, а окно экрана до ближайшего понедельника не дотягивается.
                  </span>
                </div>
              ) : null}

              {periods.map((period) => (
                <PeriodCard
                  key={period.period_start_local}
                  brand={brand}
                  period={period}
                  formats={formats}
                  onSaved={plan.refetch}
                />
              ))}
            </div>
          ) : null}

          {/* ══ сетка недель ══ */}
          <div className={styles['weeks']}>
            {schedule.loading ? <div className="micro">Загружаем расписание…</div> : null}

            {schedule.error === undefined ? null : (
              <div className="val val-err">
                <span>Расписание не загрузилось: {schedule.error.message}</span>
              </div>
            )}

            {!schedule.loading && schedule.error === undefined && slots.length === 0 ? (
              <div className="val val-info">
                <span>
                  На ближайшие 4 недели слотов нет: сетка расписания бренда не материализована.
                  Слоты задаёт конфиг бренда, а материализует их обслуживающий джоб — либо добавьте
                  слот кнопкой «Настроить слоты».
                </span>
              </div>
            ) : null}

            {weeks.map((week) => {
              const taken = week.slots.filter((s) => s.state !== 'free').length;
              return (
                <div
                  key={Number.isNaN(week.start) ? 'unparsed' : week.start}
                  className="card-flat stack"
                  style={{ gap: 10 }}
                >
                  <div className={styles['weekHead']}>
                    <div className="h3">
                      {Number.isNaN(week.start)
                        ? 'Слоты с нераспознанной минутой'
                        : weekLabel(week.start)}
                    </div>
                    <div style={{ flex: 1 }} />
                    <span className="meta">
                      занято {taken} из {week.slots.length}
                    </span>
                  </div>
                  <ul className={styles['slots']}>
                    {week.slots.map((slot) => (
                      <SlotRow
                        key={slot.slot_id}
                        brand={brand}
                        slot={slot}
                        editing={editing}
                        busy={busy}
                        moving={moving}
                        target={hovered === slot.slot_id}
                        on={handlers}
                      />
                    ))}
                  </ul>
                </div>
              );
            })}

            <div className="micro">
              Пост попадает в слот только через утверждение человеком: бэклог → «Утвердить…» → выбор
              слота. Перенос между слотами отзывает старый ключ и выпускает новый, поэтому проходит
              те же проверки, что и утверждение.
            </div>
          </div>
        </div>
      </div>

      {cancelling === null ? null : (
        <Modal
          title="Отменить публикацию?"
          subtitle={slotLabel(cancelling.fromLocal)}
          width={480}
          onClose={() => {
            setCancelling(null);
          }}
        >
          <div className="stack" style={{ gap: 8 }}>
            <span className="small" style={{ color: 'var(--ink)', fontWeight: 600 }}>
              {cancelling.title}
            </span>
            <span className="micro">
              Ключ будет отозван, слот освободится, пост вернётся в бэклог одобренным. Новый слот
              потребует нового утверждения.
            </span>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                setCancelling(null);
              }}
            >
              Не отменять
            </button>
            <div style={{ flex: 1 }} />
            <button
              type="button"
              className="btn btn-danger"
              disabled={busy}
              onClick={() => {
                cancelPublication(cancelling);
              }}
            >
              Отменить публикацию
            </button>
          </div>
        </Modal>
      )}
    </Shell>
  );
}
